export type TodoistCloseTarget = {
  taskIds?: string[]
  topicIds?: string[]
  chatIds?: string[]
}

export type TodoistCloseImpactTask = {
  id: string
  content: string
  parent_id: string | null
  selected: boolean
}

export type TodoistCloseImpact = {
  ok: boolean
  requested_task_ids: string[]
  selected_task_count: number
  affected_task_count: number
  implicit_subtask_count: number
  tasks: TodoistCloseImpactTask[]
}

function unique(values: string[] | undefined): string[] {
  return [...new Set((values ?? []).map((value) => value.trim()).filter(Boolean))]
}

export function isTodoistSourceKind(sourceKind: string | null | undefined): boolean {
  return sourceKind === 'todoist' || sourceKind === 'todoist_task'
}

export async function fetchTodoistCloseImpact(
  target: TodoistCloseTarget,
): Promise<TodoistCloseImpact> {
  const taskIds = unique(target.taskIds)
  const topicIds = unique(target.topicIds)
  const chatIds = unique(target.chatIds)
  const response = await fetch('/api/todoist/close-impact', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      task_ids: taskIds,
      topic_ids: topicIds,
      chat_ids: chatIds,
    }),
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { detail?: unknown } | null
    const detail = typeof body?.detail === 'string' ? body.detail : `HTTP ${response.status}`
    throw new Error(`Could not check Todoist subtasks; nothing was closed. ${detail}`)
  }
  return response.json() as Promise<TodoistCloseImpact>
}

export function todoistCloseAlertText(impact: TodoistCloseImpact): string {
  const total = impact.affected_task_count
  const implicit = impact.implicit_subtask_count
  const selected = impact.selected_task_count
  const heading = implicit > 0
    ? `Alert: this will complete ${total} Todoist tasks, including ${implicit} ${implicit === 1 ? 'subtask' : 'subtasks'}.`
    : `Alert: this will complete ${total} Todoist tasks.`

  const hiddenTasks = impact.tasks.filter((task) => !task.selected)
  const preview = hiddenTasks
    .slice(0, 5)
    .map((task) => `• ${task.content || 'Untitled task'}`)
  if (hiddenTasks.length > preview.length) {
    preview.push(`• …and ${hiddenTasks.length - preview.length} more`)
  }

  const selectionNote = selected > 1
    ? `You selected ${selected} of these tasks.`
    : 'Todoist completes active subtasks automatically when their parent closes.'
  return [heading, selectionNote, preview.join('\n'), 'Continue?']
    .filter(Boolean)
    .join('\n\n')
}

/** Return true when the close is safe to continue or Mark confirms its scope. */
export async function confirmTodoistClose(target: TodoistCloseTarget): Promise<boolean> {
  const impact = await fetchTodoistCloseImpact(target)
  if (impact.affected_task_count <= 1) return true
  return window.confirm(todoistCloseAlertText(impact))
}
